import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { StatusCodes } from 'http-status-codes';

import { notify } from 'utils/toast/createToast';

import { ErrorResponse } from 'types/api';

import { AlertType, ErrorMessageType } from 'constants/enums';
import { ACCESS_FORBIDDEN_MESSAGE_TEXT } from 'constants/notifications';

interface Message {
  message?: string;
  description?: string;
}

export interface catchErrorProps {
  type?: ErrorMessageType;
  error?: unknown;
  isShowDefaultMessage?: boolean;
  default?: Message;
  messages?: Record<number, Message>;
  additionalErrorMessageMap?: Record<number, Message>;
  consoleMessage?: string;
  duration?: number;
}

const ERROR_TYPE_TO_ALERT_TYPE_MAPPING = {
  [ErrorMessageType.Error]: AlertType.Error,
  [ErrorMessageType.Warning]: AlertType.Warning,
  [ErrorMessageType.Info]: AlertType.Info,
  [ErrorMessageType.Success]: AlertType.Success
};

const useErrorCatch = () => {
  const { t } = useTranslation();

  const ADDITIONAL_REQUIRED_NOTIFICATIONS_MAP: Record<number, Message> = {
    [StatusCodes.FORBIDDEN]: { message: t(ACCESS_FORBIDDEN_MESSAGE_TEXT) }
  };

  const catchError = useCallback(
    ({
      type = ErrorMessageType.Error,
      error,
      isShowDefaultMessage = true,
      default: defaultMessage,
      consoleMessage,
      messages,
      additionalErrorMessageMap
    }: catchErrorProps) => {
      if (consoleMessage) {
        console.error(consoleMessage);
      }

      //Set defaultMessage as message for notification
      let notificationMessage = defaultMessage;

      //If no defaultMessage set error.message (if error exists) as message
      if (error && !notificationMessage) {
        notificationMessage = { message: (error as ErrorResponse).message };
      }

      //If error code in errorMessageMap - set this massage instead of defaultMessage
      if (error && messages?.[(error as ErrorResponse).status]) {
        notificationMessage = messages[(error as ErrorResponse).status];
      }

      if (isShowDefaultMessage && notificationMessage) {
        notify({
          type: ERROR_TYPE_TO_ALERT_TYPE_MAPPING[type],
          ...notificationMessage
        });
      }

      // Additional error message show if have status in
      // additionalErrorMessageMap or ADDITIONAL_REQUIRED_NOTIFICATIONS_MAP
      if (error) {
        const additionalMessage =
          additionalErrorMessageMap?.[(error as ErrorResponse).status] ||
          ADDITIONAL_REQUIRED_NOTIFICATIONS_MAP[
            (error as ErrorResponse).status
          ];

        // Show additional error message from ADDITIONAL_REQUIRED_NOTIFICATIONS_MAP only if message is different
        if (
          additionalMessage &&
          (additionalMessage.description !== notificationMessage?.description ||
            additionalMessage.message !== notificationMessage?.message)
        ) {
          notify({
            type: ERROR_TYPE_TO_ALERT_TYPE_MAPPING[type],
            ...notificationMessage
          });
        }
      }
    },
    []
  );

  return catchError;
};

export default useErrorCatch;
